import type { ItineraryItem } from '@/services/itinerary';
import type { Trip } from '@/services/trips';
import { buildExternalMapsSearchQuery } from '@/utils/externalMapsQuery';
import { getDateKeyForTripDay, getItemTripCalendarDateKey, getTripEndDateKey, getTripStartDateKey } from '@/utils/tripCalendar';
import { getTripDayContext } from '@/utils/tripDayContext';
import {
  resolveItemEndInstant,
  resolveItemStartInstant,
  resolveTripSurfaceTimezone,
  toDateKeyInTimezone,
} from '@/utils/tripItemSchedule';

export type AppleQuickAction =
  | 'open_next_trip'
  | 'show_today_itinerary'
  | 'show_documents'
  | 'open_templates'
  | 'add_expense'
  | 'add_checklist_item';

export type AppleSurfaceQuickAction = {
  action: AppleQuickAction;
  route: string;
  label: string;
};

export type AppleNextTripSnapshot = {
  tripId: string;
  title: string;
  destinationLabel: string;
  startDateIso: string;
  endDateIso: string;
  countdownLabel: string;
  /** Trip calendar anchor (YYYY-MM-DD) for native day math. */
  tripStartDateKey?: string;
  tripEndDateKey?: string;
};

export type AppleTodayBriefingSnapshot = {
  tripId: string;
  tripDateIso: string;
  timezone: string;
  firstItemTitle?: string;
  nextItemTitle?: string;
  nextItemStartIso?: string;
  weatherSummary?: string;
  alertCount: number;
};

export type AppleTripSurfaceMode = 'empty' | 'preTrip' | 'travelDay' | 'postTrip';

export type AppleTripSurfaceItemSnapshot = {
  id: string;
  title: string;
  category: string;
  timeLabel: string;
  startDateIso: string;
  /** Scheduled end instant for native widget/Live Activity time-based refresh. */
  endDateIso?: string;
  locationLabel?: string;
  /** Same query text used by in-app “View on map” / directions. */
  navigationQuery?: string;
  latitude?: number;
  longitude?: number;
  status?: string;
  /** Trip calendar bucket (YYYY-MM-DD) aligned with in-app grouping. */
  tripDateKey?: string;
};

export type AppleIndexedTripSnapshot = {
  tripId: string;
  title: string;
  destinationLabel: string;
  dayKey?: string;
};

export type AppleTripSurfaceDaySnapshot = {
  dateKey: string;
  label: string;
  dayNumber: number | null;
  totalDays: number;
};

export type AppleTripSurfaceSummarySnapshot = {
  itemCount: number;
  completedCount: number;
  remainingCount: number;
  currentItem: AppleTripSurfaceItemSnapshot | null;
  nextItem: AppleTripSurfaceItemSnapshot | null;
  afterNextItem: AppleTripSurfaceItemSnapshot | null;
};

export type AppleTripSurfaceLiveActivitySnapshot = {
  eligible: boolean;
  state: 'inactive' | 'active' | 'ended';
  startAfterIso?: string;
  staleAfterIso?: string;
  endAfterIso?: string;
  updateCadenceSeconds: number;
};

export type AppleTripSurfaceSnapshotV1 = {
  version: 1;
  mode: AppleTripSurfaceMode;
  generatedAtIso: string;
  timezone: string;
  trip: AppleNextTripSnapshot | null;
  day: AppleTripSurfaceDaySnapshot | null;
  summary: AppleTripSurfaceSummarySnapshot | null;
  /** Today's scheduled items so the widget extension can recompute current/next while the app is closed. */
  todayItems?: AppleTripSurfaceItemSnapshot[];
  /** Full trip scheduled items for native day rollover and cross-day next resolution. */
  allItems?: AppleTripSurfaceItemSnapshot[];
  countdownLabel: string;
  tapUrl: string;
  planRoute: string;
  liveActivity: AppleTripSurfaceLiveActivitySnapshot;
  widgetFamilies: Array<'systemSmall' | 'accessoryRectangular'>;
  /** Upcoming trips for Siri / App Entity string matching (title + destination). */
  indexedTrips?: AppleIndexedTripSnapshot[];
};

export type AppleSurfaceSnapshot = {
  generatedAtIso: string;
  nextTrip: AppleNextTripSnapshot | null;
  todayBriefing: AppleTodayBriefingSnapshot | null;
  quickActions: AppleSurfaceQuickAction[];
  tripSurface: AppleTripSurfaceSnapshotV1;
};

export type LiveActivityGateReason =
  | 'ok'
  | 'feature_disabled'
  | 'missing_itinerary'
  | 'missing_timezone'
  | 'missing_next_item'
  | 'stale_trip_data';

export type LiveActivityGateResult = {
  passed: boolean;
  reasons: LiveActivityGateReason[];
};

const MILLIS_IN_DAY = 24 * 60 * 60 * 1000;
const LIVE_ACTIVITY_FALLBACK_END_HOUR = 23;
const LIVE_ACTIVITY_FALLBACK_END_MINUTE = 59;

const computeCountdownLabel = (trip: Trip, now: Date): string => {
  const deltaDays = Math.ceil((trip.startDate.getTime() - now.getTime()) / MILLIS_IN_DAY);
  if (deltaDays <= 0) return 'Starts today';
  if (deltaDays === 1) return 'Starts tomorrow';
  return `Starts in ${deltaDays} days`;
};

const startOfLocalDay = (date: Date): Date => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};

const endOfLocalDay = (date: Date): Date => {
  const next = new Date(date);
  next.setHours(LIVE_ACTIVITY_FALLBACK_END_HOUR, LIVE_ACTIVITY_FALLBACK_END_MINUTE, 59, 999);
  return next;
};

const diffCalendarDays = (from: Date, to: Date): number =>
  Math.round((startOfLocalDay(to).getTime() - startOfLocalDay(from).getTime()) / MILLIS_IN_DAY);

const encodeQuery = (params: Record<string, string | number | undefined>): string => {
  const pairs = Object.entries(params).flatMap(([key, value]) => {
    if (value === undefined) return [];
    return `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`;
  });
  return pairs.join('&');
};

export const buildApplePlanDeepLink = (params: {
  tripId?: string;
  dayKey?: string;
  source?: 'widget' | 'live_activity' | 'debug';
}): string => {
  const query = encodeQuery({
    target: 'plan',
    tripId: params.tripId,
    day: params.dayKey,
    appleSurface: params.source ?? 'widget',
  });
  return `tripvio://nav${query ? `?${query}` : ''}`;
};

export const buildAppleTripsDeepLink = (params: {
  source?: 'widget' | 'live_activity' | 'debug';
} = {}): string => {
  const query = encodeQuery({
    target: 'trips',
    appleSurface: params.source ?? 'widget',
  });
  return `tripvio://nav${query ? `?${query}` : ''}`;
};

const resolveDestinationLabel = (trip: Trip): string => {
  const destination = trip.destinations?.[0];
  if (destination?.country) return `${destination.city}, ${destination.country}`;
  if (destination?.city) return destination.city;
  return trip.destination || trip.name;
};

const buildPlanRoute = (params: { tripId?: string; dayKey?: string }): string => {
  const query = encodeQuery({
    tripId: params.tripId,
    day: params.dayKey,
  });
  return `/(tabs)/plan${query ? `?${query}` : ''}`;
};

const mergeItemTimeOntoDate = (item: ItineraryItem, timeZone: string, trip: Trip): Date =>
  resolveItemStartInstant(item, timeZone, trip);

const mergeItemEndTimeOntoDate = (item: ItineraryItem, timeZone: string, trip: Trip): Date | null =>
  resolveItemEndInstant(item, timeZone, trip);

const isScheduledItem = (item: ItineraryItem): boolean =>
  Boolean(item.startTime && item.startTime !== 'flexible');

const formatItemTimeLabel = (item: ItineraryItem): string => {
  if (!item.startTime || item.startTime === 'flexible') return 'Flexible';
  const [hourRaw, minuteRaw] = item.startTime.split(':');
  const hour = Number.parseInt(hourRaw, 10);
  const minute = Number.parseInt(minuteRaw ?? '0', 10);
  if (!Number.isFinite(hour)) return item.startTime;
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${String(Number.isFinite(minute) ? minute : 0).padStart(2, '0')} ${ampm}`;
};

const toSurfaceItem = (item: ItineraryItem, timeZone: string, trip: Trip): AppleTripSurfaceItemSnapshot => {
  const navigationQuery = buildExternalMapsSearchQuery(item).trim();
  const latitude = item.coordinates?.latitude;
  const longitude = item.coordinates?.longitude;
  const endDate = mergeItemEndTimeOntoDate(item, timeZone, trip);
  return {
    id: item.id,
    title: item.title,
    category: item.category,
    timeLabel: formatItemTimeLabel(item),
    startDateIso: mergeItemTimeOntoDate(item, timeZone, trip).toISOString(),
    endDateIso: endDate?.toISOString(),
    tripDateKey: getItemTripCalendarDateKey(trip, item),
    locationLabel: item.location || item.address || undefined,
    navigationQuery: navigationQuery.length > 0 ? navigationQuery : undefined,
    latitude: typeof latitude === 'number' && Number.isFinite(latitude) ? latitude : undefined,
    longitude: typeof longitude === 'number' && Number.isFinite(longitude) ? longitude : undefined,
    status: item.status,
  };
};

const endOfLocalDayForIndexedTrip = (date: Date): Date => {
  const end = new Date(date);
  end.setHours(23, 59, 59, 999);
  return end;
};

/** Upcoming / in-progress trips exposed to Siri entity resolution. */
export const buildAppleIndexedTrips = (trips: Trip[], now: Date = new Date()): AppleIndexedTripSnapshot[] =>
  trips
    .filter((trip) => !trip.isArchived && endOfLocalDayForIndexedTrip(trip.endDate).getTime() >= now.getTime())
    .sort((a, b) => a.startDate.getTime() - b.startDate.getTime())
    .slice(0, 12)
    .map((trip) => {
      const timeZone = resolveTripSurfaceTimezone(trip);
      const context = getTripDayContext(trip, now, { timeZone });
      const dayKey =
        context.state === 'upcoming' ? getTripStartDateKey(trip) : context.todayKey;
      return {
        tripId: trip.id,
        title: trip.name,
        destinationLabel: resolveDestinationLabel(trip),
        dayKey,
      };
    });

const sortItemsBySchedule = (items: ItineraryItem[], timeZone: string, trip: Trip): ItineraryItem[] =>
  [...items].sort(
    (a, b) =>
      mergeItemTimeOntoDate(a, timeZone, trip).getTime() - mergeItemTimeOntoDate(b, timeZone, trip).getTime(),
  );

const groupSurfaceItemsByDate = (items: ItineraryItem[], trip: Trip, timeZone: string): Map<string, ItineraryItem[]> => {
  const grouped = new Map<string, ItineraryItem[]>();
  for (const item of items) {
    if (!item?.startDate) continue;
    const keys = new Set<string>([getItemTripCalendarDateKey(trip, item)]);
    if (item.endDate) {
      keys.add(getItemTripCalendarDateKey(trip, { startDate: item.endDate }));
    }
    keys.forEach((dateKey) => {
      const bucket = grouped.get(dateKey) ?? [];
      if (!bucket.some((existing) => existing.id === item.id)) {
        bucket.push(item);
      }
      grouped.set(dateKey, bucket);
    });
  }
  grouped.forEach((bucket) => {
    bucket.sort(
      (a, b) =>
        mergeItemTimeOntoDate(a, timeZone, trip).getTime() - mergeItemTimeOntoDate(b, timeZone, trip).getTime(),
    );
  });
  return grouped;
};

const findFirstUpcomingItem = (items: ItineraryItem[], now: Date, timeZone: string, trip: Trip): ItineraryItem | null => {
  for (const item of sortItemsBySchedule(items, timeZone, trip)) {
    if (!isScheduledItem(item) || item.status === 'completed') continue;
    if (mergeItemTimeOntoDate(item, timeZone, trip).getTime() > now.getTime()) return item;
  }
  return null;
};

const findCurrentItem = (items: ItineraryItem[], now: Date, timeZone: string, trip: Trip): ItineraryItem | null => {
  for (const item of sortItemsBySchedule(items, timeZone, trip)) {
    if (!isScheduledItem(item) || item.status === 'completed') continue;
    const start = mergeItemTimeOntoDate(item, timeZone, trip);
    const end = mergeItemEndTimeOntoDate(item, timeZone, trip);
    if (!end) continue;
    if (now >= start && now <= end) return item;
  }
  return null;
};

const findItemAfter = (items: ItineraryItem[], anchor: ItineraryItem | null, timeZone: string, trip: Trip): ItineraryItem | null => {
  if (!anchor) return null;
  const sorted = sortItemsBySchedule(items, timeZone, trip);
  const anchorIndex = sorted.findIndex((item) => item.id === anchor.id);
  if (anchorIndex < 0) return null;
  for (let index = anchorIndex + 1; index < sorted.length; index += 1) {
    const candidate = sorted[index];
    if (isScheduledItem(candidate)) return candidate;
  }
  return null;
};

const chooseCurrentAndNextItems = (
  todayItems: ItineraryItem[],
  allItems: ItineraryItem[],
  now: Date,
  timeZone: string,
  trip: Trip,
): { currentItem: ItineraryItem | null; nextItem: ItineraryItem | null; afterNextItem: ItineraryItem | null } => {
  const currentItem = findCurrentItem(todayItems, now, timeZone, trip);
  const nextItem =
    findFirstUpcomingItem(todayItems, now, timeZone, trip) ?? findFirstUpcomingItem(allItems, now, timeZone, trip);
  const afterNextItem =
    findItemAfter(todayItems, nextItem, timeZone, trip) ?? findItemAfter(allItems, nextItem, timeZone, trip);
  return { currentItem, nextItem, afterNextItem };
};

const liveActivityEndDate = (trip: Trip, itineraryItems: ItineraryItem[], timeZone: string): Date => {
  const tripEndKey = getDateKeyForTripDay(trip, getTripDayContext(trip, new Date(), { timeZone }).totalDays);
  const finalTripDayItems = itineraryItems.filter(
    (item) => getItemTripCalendarDateKey(trip, item) === tripEndKey,
  );
  if (finalTripDayItems.length === 0) return endOfLocalDay(trip.endDate);
  const sortedFinalItems = sortItemsBySchedule(finalTripDayItems, timeZone, trip);
  const finalItemDate = sortedFinalItems[sortedFinalItems.length - 1];
  if (!finalItemDate) return endOfLocalDay(trip.endDate);
  const merged =
    mergeItemEndTimeOntoDate(finalItemDate, timeZone, trip) ??
    mergeItemTimeOntoDate(finalItemDate, timeZone, trip);
  return merged;
};

const updateCadenceSeconds = (
  nextItem: ItineraryItem | null,
  now: Date,
  timeZone: string,
  trip: Trip,
): number => {
  if (!nextItem) return 30 * 60;
  const minutesAway = Math.max(
    0,
    Math.round((mergeItemTimeOntoDate(nextItem, timeZone, trip).getTime() - now.getTime()) / 60000),
  );
  if (minutesAway <= 30) return 60;
  if (minutesAway <= 120) return 5 * 60;
  return 30 * 60;
};

export const buildAppleTripSurfaceSnapshot = (opts: {
  trip?: Trip | null;
  itineraryItems?: ItineraryItem[];
  indexedTrips?: AppleIndexedTripSnapshot[];
  now?: Date;
  source?: 'widget' | 'live_activity' | 'debug';
  /** Destination IANA timezone; defaults to trip destination lookup. */
  tripTimeZone?: string;
}): AppleTripSurfaceSnapshotV1 => {
  const now = opts.now ?? new Date();
  const trip = opts.trip ?? null;
  const timezone = trip
    ? opts.tripTimeZone ?? resolveTripSurfaceTimezone(trip)
    : Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  if (!trip) {
    return {
      version: 1,
      mode: 'empty',
      generatedAtIso: now.toISOString(),
      timezone,
      trip: null,
      day: null,
      summary: null,
      countdownLabel: 'Plan your next trip',
      tapUrl: buildAppleTripsDeepLink({ source: opts.source ?? 'widget' }),
      planRoute: '/(tabs)',
      liveActivity: {
        eligible: false,
        state: 'inactive',
        updateCadenceSeconds: 30 * 60,
      },
      widgetFamilies: ['systemSmall', 'accessoryRectangular'],
      indexedTrips: opts.indexedTrips,
    };
  }

  const context = getTripDayContext(trip, now, { timeZone: timezone });
  const mode: AppleTripSurfaceMode =
    context.state === 'upcoming'
      ? 'preTrip'
      : context.state === 'completed'
        ? 'postTrip'
        : 'travelDay';
  const dayKey =
    context.state === 'upcoming'
      ? getTripStartDateKey(trip)
      : context.todayKey;
  const allScheduledItems = sortItemsBySchedule(
    (opts.itineraryItems ?? []).filter(isScheduledItem),
    timezone,
    trip,
  );
  const grouped = groupSurfaceItemsByDate(opts.itineraryItems ?? [], trip, timezone);
  const todayItems = grouped.get(dayKey) ?? [];
  const sortedTodayItems = sortItemsBySchedule(todayItems, timezone, trip);
  const completedCount = sortedTodayItems.filter((item) => item.status === 'completed').length;
  const { currentItem, nextItem, afterNextItem } = chooseCurrentAndNextItems(
    sortedTodayItems,
    opts.itineraryItems ?? [],
    now,
    timezone,
    trip,
  );
  const endAfter = liveActivityEndDate(trip, opts.itineraryItems ?? [], timezone);
  const liveState =
    mode !== 'travelDay'
      ? 'inactive'
      : now.getTime() > endAfter.getTime()
        ? 'ended'
        : 'active';
  const dayLabel = context.currentDay
    ? `Day ${context.currentDay}`
    : context.daysUntilStart === 0
      ? 'Starts today'
      : computeCountdownLabel(trip, now);
  const countdownLabel =
    mode === 'preTrip'
      ? computeCountdownLabel(trip, now)
      : currentItem
        ? `Now: ${currentItem.title}`
        : nextItem
          ? `${nextItem.title} ${formatRelativeStartLabel(mergeItemTimeOntoDate(nextItem, timezone, trip), now)}`
          : mode === 'postTrip'
            ? 'Trip ended'
            : 'No more plans today';
  const tripSnapshot: AppleNextTripSnapshot = {
    tripId: trip.id,
    title: trip.name,
    destinationLabel: resolveDestinationLabel(trip),
    startDateIso: trip.startDate.toISOString(),
    endDateIso: trip.endDate.toISOString(),
    countdownLabel: computeCountdownLabel(trip, now),
    tripStartDateKey: getTripStartDateKey(trip),
    tripEndDateKey: getTripEndDateKey(trip),
  };

  return {
    version: 1,
    mode,
    generatedAtIso: now.toISOString(),
    timezone,
    trip: tripSnapshot,
    day: {
      dateKey: dayKey,
      label: dayLabel,
      dayNumber: context.currentDay,
      totalDays: context.totalDays,
    },
    summary: {
      itemCount: sortedTodayItems.length,
      completedCount,
      remainingCount: Math.max(0, sortedTodayItems.length - completedCount),
      currentItem: currentItem ? toSurfaceItem(currentItem, timezone, trip) : null,
      nextItem: nextItem ? toSurfaceItem(nextItem, timezone, trip) : null,
      afterNextItem: afterNextItem ? toSurfaceItem(afterNextItem, timezone, trip) : null,
    },
    todayItems: sortedTodayItems.filter(isScheduledItem).map((item) => toSurfaceItem(item, timezone, trip)),
    allItems: allScheduledItems.map((item) => toSurfaceItem(item, timezone, trip)),
    countdownLabel,
    tapUrl: buildApplePlanDeepLink({ tripId: trip.id, dayKey, source: opts.source ?? 'widget' }),
    planRoute: buildPlanRoute({ tripId: trip.id, dayKey }),
    liveActivity: {
      eligible: mode === 'travelDay' && liveState === 'active',
      state: liveState,
      startAfterIso: startOfLocalDay(trip.startDate).toISOString(),
      staleAfterIso: new Date(now.getTime() + updateCadenceSeconds(nextItem, now, timezone, trip) * 1000).toISOString(),
      endAfterIso: endAfter.toISOString(),
      updateCadenceSeconds: updateCadenceSeconds(nextItem, now, timezone, trip),
    },
    widgetFamilies: ['systemSmall', 'accessoryRectangular'],
    indexedTrips: opts.indexedTrips,
  };
};

const formatRelativeStartLabel = (start: Date, now: Date): string => {
  const minutes = Math.round((start.getTime() - now.getTime()) / 60000);
  if (minutes <= -60) return 'started earlier';
  if (minutes < 0) return 'is underway';
  if (minutes === 0) return 'starts now';
  if (minutes < 60) return `starts in ${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  return `starts in ${hours}h${remaining > 0 ? ` ${remaining}m` : ''}`;
};

const buildQuickActions = (tripId?: string): AppleSurfaceQuickAction[] => {
  const actions: AppleSurfaceQuickAction[] = [
    {
      action: 'open_templates',
      route: '/(tabs)?openTemplates=1',
      label: 'Open templates',
    },
  ];
  if (!tripId) return actions;
  actions.unshift(
    {
      action: 'open_next_trip',
      route: `/(tabs)/plan?tripId=${tripId}`,
      label: 'Open next trip',
    },
    {
      action: 'show_today_itinerary',
      route: `/(tabs)/plan?tripId=${tripId}&focus=today`,
      label: 'Today itinerary',
    },
    {
      action: 'show_documents',
      route: `/(tabs)/more?tab=documents&tripId=${tripId}`,
      label: 'Trip documents',
    },
    {
      action: 'add_expense',
      route: `/(tabs)/wallet?tripId=${tripId}&intent=add-expense`,
      label: 'Add expense',
    },
    {
      action: 'add_checklist_item',
      route: `/(tabs)/plan?tripId=${tripId}&intent=add-checklist`,
      label: 'Add checklist item',
    },
  );
  return actions;
};

export const buildAppleSurfaceSnapshot = (opts: {
  nextTrip?: Trip | null;
  dayBriefing?: {
    tripId: string;
    todayItems: ItineraryItem[];
    nextItem?: ItineraryItem | null;
    weather?: { tempHigh: number; tempLow: number; condition: string } | null;
    items: unknown[];
  } | null;
  itineraryItems?: ItineraryItem[];
  alertCount?: number;
  now?: Date;
}): AppleSurfaceSnapshot => {
  const now = opts.now ?? new Date();
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const nextTrip = opts.nextTrip
    ? {
        tripId: opts.nextTrip.id,
        title: opts.nextTrip.name,
        destinationLabel: resolveDestinationLabel(opts.nextTrip),
        startDateIso: opts.nextTrip.startDate.toISOString(),
        endDateIso: opts.nextTrip.endDate.toISOString(),
        countdownLabel: computeCountdownLabel(opts.nextTrip, now),
      }
    : null;

  const briefingTripId = opts.dayBriefing?.tripId ?? opts.nextTrip?.id;
  const firstItem = opts.dayBriefing?.todayItems[0];
  const nextItem = opts.dayBriefing?.nextItem ?? undefined;
  const todayBriefing = briefingTripId
    ? {
        tripId: briefingTripId,
        tripDateIso: now.toISOString(),
        timezone,
        firstItemTitle: firstItem?.title,
        nextItemTitle: nextItem?.title,
        nextItemStartIso: nextItem?.startDate?.toISOString(),
        weatherSummary: opts.dayBriefing?.weather
          ? `${Math.round(opts.dayBriefing.weather.tempHigh)}°/${Math.round(opts.dayBriefing.weather.tempLow)}° ${opts.dayBriefing.weather.condition}`
          : undefined,
        alertCount: Math.max(0, opts.alertCount ?? opts.dayBriefing?.items.length ?? 0),
      }
    : null;

  return {
    generatedAtIso: now.toISOString(),
    nextTrip,
    todayBriefing,
    quickActions: buildQuickActions(nextTrip?.tripId ?? briefingTripId),
    tripSurface: buildAppleTripSurfaceSnapshot({
      trip: opts.nextTrip ?? null,
      itineraryItems: opts.itineraryItems ?? opts.dayBriefing?.todayItems ?? [],
      now,
    }),
  };
};

export const evaluateLiveActivityGate = (opts: {
  trip?: Trip | null;
  itineraryItems?: ItineraryItem[];
  now?: Date;
  enabled: boolean;
}): LiveActivityGateResult => {
  if (!opts.enabled) {
    return { passed: false, reasons: ['feature_disabled'] };
  }
  const trip = opts.trip;
  if (!trip) {
    return { passed: false, reasons: ['stale_trip_data'] };
  }
  const reasons: LiveActivityGateReason[] = [];
  const timezone = resolveTripSurfaceTimezone(trip);
  if (!timezone) {
    reasons.push('missing_timezone');
  }
  const items = opts.itineraryItems ?? [];
  if (items.length === 0) {
    reasons.push('missing_itinerary');
  }
  const now = opts.now ?? new Date();
  const nextItem = items
    .filter((item) => resolveItemStartInstant(item, timezone, trip).getTime() >= now.getTime() - 60 * 60 * 1000)
    .sort(
      (a, b) =>
        resolveItemStartInstant(a, timezone, trip).getTime() -
        resolveItemStartInstant(b, timezone, trip).getTime(),
    )[0];
  const context = getTripDayContext(trip, now, { timeZone: timezone });
  if (!nextItem && context.state === 'active') {
    reasons.push('missing_next_item');
  }
  if (!trip.updatedAt || now.getTime() - trip.updatedAt.getTime() > 36 * 60 * 60 * 1000) {
    reasons.push('stale_trip_data');
  }
  if (reasons.length === 0) {
    return { passed: true, reasons: ['ok'] };
  }
  return { passed: false, reasons };
};

export const logLiveActivityGateDecision = async (tripId: string, result: LiveActivityGateResult): Promise<void> => {
  const { logAnalyticsEvent } = await import('@/services/analyticsLogger');
  await logAnalyticsEvent('live_activity_gate_evaluated', {
    tripId,
    passed: result.passed,
    reasons: result.reasons,
  });
};
